import { useState, useEffect, useMemo, memo } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { Menu, X, Bell, ChevronDown, Sparkles, LogOut, Search, Mic, User, Lock, LayoutDashboard, Package, RotateCcw } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import AIChat from "@/components/AIChat";
import { DeliverTo } from "@/components/DeliverTo";
import { NotificationBell } from "@/components/NotificationBell";
import SellerLegalGate from "@/components/SellerLegalGate";
import { RecentlyViewedNudge } from "@/components/RecentlyViewedNudge";
import { MobileVerifyBanner } from "@/components/MobileVerifyBanner";

const NavItemLink = memo(function NavItemLink({ item, onNavigate, badge }) {
  return (
    <NavLink to={item.to} end={item.end} onClick={onNavigate}
      data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
      className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
      <item.icon className="h-[18px] w-[18px] shrink-0" />
      <span className="truncate">{item.label}</span>
      {badge > 0 && <span data-testid={`nav-badge-${item.badgeKey}`} className="ml-auto inline-flex min-w-[18px] items-center justify-center rounded-full bg-rose-500 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">{badge > 99 ? "99+" : badge}</span>}
    </NavLink>
  );
});

function NavList({ nav, onNavigate, storageKey = "nav", badges, defaultOpenSections }) {
  const hasSections = nav.some((i) => i.section);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const stored = localStorage.getItem(`navcollapse:${storageKey}`);
      if (stored != null) return new Set(JSON.parse(stored));
      // First visit: collapse every section except the default-open ones.
      if (defaultOpenSections) {
        const secs = [...new Set(nav.map((i) => i.section).filter(Boolean))];
        return new Set(secs.filter((s) => !defaultOpenSections.includes(s)));
      }
      return new Set();
    } catch { return new Set(); }
  });
  const toggle = (sec) => setCollapsed((prev) => {
    const next = new Set(prev);
    next.has(sec) ? next.delete(sec) : next.add(sec);
    localStorage.setItem(`navcollapse:${storageKey}`, JSON.stringify([...next]));
    return next;
  });
  const groups = useMemo(() => {
    const gs = [];
    nav.forEach((item) => {
      const sec = item.section || "";
      let g = gs.find((x) => x.sec === sec);
      if (!g) { g = { sec, items: [] }; gs.push(g); }
      g.items.push(item);
    });
    return gs;
  }, [nav]);
  if (!hasSections) {
    return (
      <nav className="custom-scroll flex-1 space-y-1 overflow-y-auto px-3 pb-6">
        {nav.filter((item) => !item.hidden).map((item) => <NavItemLink key={item.to} item={item} onNavigate={onNavigate} badge={item.badgeKey ? badges?.[item.badgeKey] : 0} />)}
      </nav>
    );
  }
  return (
    <nav className="custom-scroll flex-1 space-y-1 overflow-y-auto px-3 pb-6">
      {groups.map((g) => {
        const isOpen = !collapsed.has(g.sec);
        return (
          <div key={g.sec}>
            <button onClick={() => toggle(g.sec)} data-testid={`navsection-${g.sec.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="flex w-full items-center justify-between px-3 pb-1 pt-4 text-[10px] font-semibold uppercase tracking-wider text-white/35 transition-colors hover:text-white/70">
              <span>{g.sec}</span>
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isOpen ? "" : "-rotate-90"}`} />
            </button>
            {isOpen && g.items.filter((item) => !item.hidden).map((item) => <NavItemLink key={item.to} item={item} onNavigate={onNavigate} badge={item.badgeKey ? badges?.[item.badgeKey] : 0} />)}
          </div>
        );
      })}
    </nav>
  );
}

export default function DashboardLayout({ nav, bottomNav, portalLabel, aiSurface = "general", aiPlaceholder = "Ask your AI Secretary anything...", defaultOpenSections }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    // Keep private dashboards (customer/seller/admin) out of search indexes.
    let m = document.querySelector('meta[name="robots"]');
    if (!m) { m = document.createElement("meta"); m.name = "robots"; document.head.appendChild(m); }
    m.content = "noindex, nofollow";
    return () => { const e = document.querySelector('meta[name="robots"]'); if (e) e.remove(); };
  }, []);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [championStreak, setChampionStreak] = useState(0);
  const [trial, setTrial] = useState(null);
  const [navBadges, setNavBadges] = useState({});
  useEffect(() => {
    if (aiSurface !== "agent") return;
    let alive = true;
    const load = () => api.get("/marketplace/seller/orders/summary").then((r) => {
      if (!alive) return;
      const s = r.data.summary || {};
      setNavBadges({ seller_requests: (s.return_requests || 0) + (s.replacement_requests || 0) });
    }).catch(() => {});
    load();
    const id = setInterval(load, 30000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    window.addEventListener("account-updated", onFocus);
    return () => { alive = false; clearInterval(id); window.removeEventListener("focus", onFocus); window.removeEventListener("account-updated", onFocus); };
  }, [aiSurface]);
  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  useEffect(() => {
    if (aiSurface === "admin") return;
    api.get("/subscription/trial-status").then((r) => setTrial(r.data)).catch(() => {});
  }, [aiSurface, location.pathname]);

  useEffect(() => {
    const onExpired = () => setTrial((t) => ({ ...(t || {}), locked: true, trial_expired: true, trial_active: false }));
    window.addEventListener("trial-expired", onExpired);
    return () => window.removeEventListener("trial-expired", onExpired);
  }, []);

  const subscribePath = aiSurface === "agent" ? "/agent/subscription" : "/app/subscription";
  const onBillingPage = location.pathname.includes("/subscription") || location.pathname.includes("/profile");
  const showLockout = trial?.locked && !onBillingPage;

  useEffect(() => {
    if (aiSurface !== "customer") { setChampionStreak(0); return; }
    api.get("/referral/season-champion").then((r) => setChampionStreak(r.data?.champion?.is_you ? (r.data.champion.streak || 0) : 0)).catch(() => {});
  }, [aiSurface]);

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#F4F6FB]">
      {user?.role === "travel_agent" && <SellerLegalGate />}
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-[260px] flex-col border-r border-white/10 md:flex" style={{ backgroundColor: "#0b0a1f", backgroundImage: "radial-gradient(70% 30% at 20% 0%, rgba(37,99,235,.4), transparent 60%), radial-gradient(60% 45% at 100% 100%, rgba(219,39,119,.22), transparent 60%)" }}>
        <div className="px-5 py-5" style={{ backgroundColor: "#0b0a1f", backgroundImage: "radial-gradient(80% 70% at 20% 0%, rgba(37,99,235,.5), transparent 60%), radial-gradient(80% 80% at 100% 30%, rgba(124,58,237,.45), transparent 60%)" }}>
          <span className="inline-block w-fit [filter:drop-shadow(0_0_4px_rgba(255,255,255,0.45))_drop-shadow(0_0_14px_rgba(124,110,255,0.5))]"><Logo variant="md" /></span>
          {portalLabel && (
            <div className="mt-3 inline-flex rounded-lg border border-white/20 bg-white/10 px-2.5 py-1 text-xs font-semibold text-white" data-testid="portal-label">
              {portalLabel}
            </div>
          )}
        </div>
        <NavList nav={nav} storageKey={aiSurface} badges={navBadges} defaultOpenSections={defaultOpenSections} />
      </aside>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-black/30 md:hidden" onClick={() => setMobileOpen(false)} />
            <motion.aside initial={{ x: -280 }} animate={{ x: 0 }} exit={{ x: -280 }} transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="fixed inset-y-0 left-0 z-50 flex w-[260px] flex-col border-r border-white/10 md:hidden" style={{ backgroundColor: "#0b0a1f", backgroundImage: "radial-gradient(70% 30% at 20% 0%, rgba(37,99,235,.4), transparent 60%), radial-gradient(60% 45% at 100% 100%, rgba(219,39,119,.22), transparent 60%)" }}>
              <div className="flex items-center justify-between px-5 py-5" style={{ backgroundColor: "#0b0a1f", backgroundImage: "radial-gradient(80% 70% at 20% 0%, rgba(37,99,235,.5), transparent 60%), radial-gradient(80% 80% at 100% 30%, rgba(124,58,237,.45), transparent 60%)" }}>
                <span className="inline-block w-fit [filter:drop-shadow(0_0_4px_rgba(255,255,255,0.45))_drop-shadow(0_0_14px_rgba(124,110,255,0.5))]"><Logo variant="md" /></span>
                <button onClick={() => setMobileOpen(false)} data-testid="mobile-close"><X className="h-5 w-5 text-white/80" /></button>
              </div>
              <NavList nav={nav} storageKey={aiSurface} onNavigate={() => setMobileOpen(false)} badges={navBadges} defaultOpenSections={defaultOpenSections} />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <div className="md:pl-[260px]">
        {/* Top bar */}
        <header className="sticky top-0 z-20 flex items-center gap-2 border-b border-slate-200 bg-white/90 px-3 py-3 backdrop-blur md:gap-3 md:px-6">
          <button className="shrink-0 md:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn">
            <Menu className="h-6 w-6 text-slate-600" />
          </button>
          <button onClick={() => setAiOpen(true)} data-testid="global-ai-bar"
            className="group flex min-w-0 flex-1 items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-4 py-2.5 text-left text-sm text-slate-400 transition hover:border-blue-300">
            <Search className="h-4 w-4 shrink-0 text-blue-500" />
            <span className="truncate">{aiPlaceholder}</span>
            <Mic className="ml-auto hidden h-4 w-4 shrink-0 text-slate-400 sm:block" />
          </button>
          {aiSurface !== "agent" && aiSurface !== "admin" && <DeliverTo />}
          <NotificationBell viewAllPath={aiSurface === "agent" ? "/agent/notifications" : aiSurface === "admin" ? "/admin/notifications" : "/app/notifications"} />
          <div className="relative shrink-0">
            <button onClick={() => setProfileOpen((o) => !o)} data-testid="profile-btn"
              className="flex items-center gap-2 rounded-full py-1 pl-1 pr-2 hover:bg-slate-100">
              <span className="relative flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full brand-gradient-bg text-sm font-semibold text-white">
                {user?.avatar ? <img src={user.avatar} alt="" className="h-full w-full object-cover" /> : initials}
              </span>
              <span className="hidden max-w-[150px] text-left lg:block">
                <span className="block truncate text-sm font-semibold leading-4 text-slate-800">Hi, {(user?.name || "").split(" ")[0]}</span>
                <span className="block truncate text-xs text-slate-400">{user?.company_name || "Suffle Member"}</span>
              </span>
              <ChevronDown className="hidden h-4 w-4 shrink-0 text-slate-400 sm:block" />
            </button>
            <AnimatePresence>
              {profileOpen && (
                <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                  className="absolute right-0 mt-2 w-52 overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-lg" data-testid="profile-menu">
                  <div className="border-b border-slate-100 px-4 py-2">
                    <p className="text-sm font-semibold text-slate-800">{user?.name}</p>
                    <p className="truncate text-xs text-slate-400">{user?.email}</p>
                    {championStreak > 0 && (
                      <button onClick={() => { setProfileOpen(false); navigate("/app/ambassador"); }} className="mt-1.5 ml-1 inline-flex items-center gap-1 rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-semibold text-orange-600 hover:bg-orange-200" data-testid="profile-streak-badge">🔥 {championStreak}-mo champion</button>
                    )}
                  </div>
                  <button onClick={() => { setProfileOpen(false); navigate(aiSurface === "agent" ? "/agent/profile" : "/app/profile"); }}
                    className="flex w-full items-center gap-2 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50">
                    <User className="h-4 w-4" /> Profile
                  </button>
                  {user?.role === "customer" && (
                    <>
                      <button onClick={() => { setProfileOpen(false); navigate("/app/home"); }} data-testid="profile-menu-dashboard"
                        className="flex w-full items-center gap-2 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50">
                        <LayoutDashboard className="h-4 w-4" /> Dashboard
                      </button>
                      <button onClick={() => { setProfileOpen(false); navigate("/app/shop-orders"); }} data-testid="profile-menu-orders"
                        className="flex w-full items-center gap-2 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50">
                        <Package className="h-4 w-4" /> Orders
                      </button>
                      <button onClick={() => { setProfileOpen(false); navigate("/app/returns"); }} data-testid="profile-menu-returns"
                        className="flex w-full items-center gap-2 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50">
                        <RotateCcw className="h-4 w-4" /> Returns
                      </button>
                    </>
                  )}
                  <button onClick={async () => { await logout(); navigate("/login"); }} data-testid="logout-btn"
                    className="flex w-full items-center gap-2 px-4 py-2 text-sm text-rose-600 hover:bg-rose-50">
                    <LogOut className="h-4 w-4" /> Log out
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </header>

        {aiSurface !== "admin" && <MobileVerifyBanner />}
        <main className="mx-auto w-full max-w-[1720px] px-4 py-5 pb-24 md:px-8 md:pb-8"><Outlet /></main>
      </div>
      <RecentlyViewedNudge />

      {/* Mobile bottom nav */}
      <nav className="fixed inset-x-0 bottom-0 z-30 flex items-center justify-around border-t border-slate-200 bg-white/95 py-1.5 backdrop-blur md:hidden">
        {(bottomNav || nav.slice(0, 5)).map((item) => (
          <NavLink key={item.to} to={item.to} end={item.end} data-testid={`bottomnav-${item.label.toLowerCase()}`}
            className={({ isActive }) => `relative flex flex-col items-center gap-0.5 rounded-lg px-3 py-1 text-[11px] ${isActive ? "text-blue-600" : "text-slate-500"}`}>
            <span className="relative"><item.icon className="h-5 w-5" />{item.badgeKey && navBadges?.[item.badgeKey] > 0 && <span data-testid={`bottomnav-badge-${item.badgeKey}`} className="absolute -right-2 -top-1.5 inline-flex min-w-[16px] items-center justify-center rounded-full bg-rose-500 px-1 text-[9px] font-bold leading-none text-white">{navBadges[item.badgeKey] > 99 ? "99+" : navBadges[item.badgeKey]}</span>}</span>{item.label}
          </NavLink>
        ))}
      </nav>

      {/* Global AI modal */}
      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent className="max-w-2xl gap-0 p-0" data-testid="global-ai-modal">
          <div className="flex items-center gap-2 border-b border-slate-100 px-5 py-4">
            <Sparkles className="h-5 w-5 text-blue-500" />
            <h3 className="font-semibold text-slate-800">Universal AI Secretary</h3>
          </div>
          <div className="h-[70vh] p-4">
            <AIChat surface={aiSurface} suggestions={[
              "Show my upcoming bookings", "What am I waiting for?", "What bills are due?", "What expires soon?",
            ]} />
          </div>
        </DialogContent>
      </Dialog>

      {/* Trial watermark — visible during the free trial, disappears on paid subscription */}
      {trial?.watermark && !trial?.locked && (
        <div data-testid="trial-watermark" className="pointer-events-none fixed bottom-20 right-3 z-40 flex items-center gap-1.5 rounded-full border border-white/10 bg-[#0b0a1f]/85 px-3 py-1.5 text-[11px] font-semibold text-white/90 shadow-lg backdrop-blur md:bottom-3">
          <Sparkles className="h-3 w-3 text-violet-300" />
          Powered By Suffle Online
        </div>
      )}

      {/* Feature lockout — hard block once the 7-day trial has expired */}
      {showLockout && (
        <div data-testid="trial-lockout-overlay" className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(11,10,31,0.94)", backgroundImage: "radial-gradient(50% 50% at 20% 15%, rgba(37,99,235,.4), transparent 60%), radial-gradient(50% 50% at 85% 20%, rgba(124,58,237,.4), transparent 60%)" }}>
          <div className="w-full max-w-md rounded-2xl border border-white/10 bg-white p-7 text-center shadow-2xl">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-amber-100"><Lock className="h-7 w-7 text-amber-600" /></div>
            <h2 className="text-2xl font-bold text-slate-900">Your free trial has ended</h2>
            <p className="mt-2 text-sm text-slate-500">Your 7-day trial of Suffle Online is over. Subscribe to a plan to unlock all your features again — your data is safe and waiting for you.</p>
            <button onClick={() => navigate(subscribePath)} data-testid="trial-subscribe-btn" className="mt-6 w-full rounded-xl brand-gradient-bg py-3 text-sm font-semibold text-white transition hover:opacity-90">Subscribe now</button>
            <button onClick={async () => { await logout(); navigate("/login"); }} data-testid="trial-logout-btn" className="mt-3 w-full rounded-xl border border-slate-200 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50">Log out</button>
          </div>
        </div>
      )}
    </div>
  );
}
